import { OnboardingJourneyNetworkRelation } from "models/v2/onboardingJourneyNetworkRelation";
import { toCompositeId } from "helpers/compositeId";
import { snakeCase, startCase, upperFirst } from "lodash";
import { User } from "features/User/models";
import { UserRoleOrEmailOption } from "components/FormElements/Fields/SelectFields/UserRoleOrEmailMultiSelect";
import { titleize, toPrefixId } from "helpers/string";
import { RRuleSet } from "rrule";
import { dateInFormat } from "helpers/time";
import { MeetingTemplate, MeetingTemplateType, TemplateAttendeeType, TemplateMeetingAttendee } from "./models";
import { MeetingTemplateSchema } from "./schema";
import { templateAttendeeToUserRoleOrEmailId } from "../MessageTemplate/helpers";
import { MeetingRecurringTimingInfo, MeetingReferenceEventTimingInfo, MeetingTimingInfo } from "./Components/Form/Timing/models";

export function toUserRoleOrEmailOption(templateMeetingAttendee: TemplateMeetingAttendee): UserRoleOrEmailOption {
  let label = templateMeetingAttendee?.emailAddress;
  let recordId;
  let name = templateMeetingAttendee?.emailAddress;
  if (templateMeetingAttendee?.attendee && templateMeetingAttendee?.attendeeType) {
    recordId = templateMeetingAttendee?.attendeeId;
    if (templateMeetingAttendee.attendeeType === "OnboardingJourneyNetworkRelation") {
      name = (templateMeetingAttendee?.attendee as OnboardingJourneyNetworkRelation)?.name;
      if (name === "new_hire") {
        label = `${startCase(name)} (${templateMeetingAttendee?.emailType})`;
      } else {
        label = startCase(name);
      }
    } else if (templateMeetingAttendee?.attendeeType === "User") {
      label = startCase();
      name = (templateMeetingAttendee?.attendee as User)?.fullName;
      if (templateMeetingAttendee?.emailType === "personal") {
        label = `${startCase(name)} (${templateMeetingAttendee?.emailType})`;
      } else {
        label = startCase(name);
      }
    }
  }
  return ({
    recordType: templateMeetingAttendee?.emailAddress ? "CustomEmail" : templateMeetingAttendee?.attendeeType as TemplateAttendeeType,
    recordId,
    id: templateAttendeeToUserRoleOrEmailId(templateMeetingAttendee),
    label: label as string,
    email: templateMeetingAttendee?.emailAddress,
    name: name as string,
    emailType: templateMeetingAttendee?.emailType,
  });
}

export function fromUserRoleOrEmailOption(userRoleOrEmailOption: UserRoleOrEmailOption) {
  const attendee = {} as Partial<TemplateMeetingAttendee>;
  if (userRoleOrEmailOption.recordType === "OnboardingJourneyNetworkRelation") {
    attendee.name = userRoleOrEmailOption?.name;
  } else if (userRoleOrEmailOption.recordType === "User") {
    attendee.fullName = userRoleOrEmailOption?.name;
  }
  const base = {
    attendeeType: userRoleOrEmailOption?.email ? undefined : userRoleOrEmailOption?.recordType,
    attendeeId: userRoleOrEmailOption?.email ? undefined : userRoleOrEmailOption?.recordId,
    emailType: userRoleOrEmailOption?.emailType,
    emailAddress: userRoleOrEmailOption?.email,
    attendeePrefixId: userRoleOrEmailOption?.recordId ? toPrefixId(snakeCase(userRoleOrEmailOption.recordType), userRoleOrEmailOption.recordId) : undefined,
  };
  if (Object.keys(attendee).length > 0) {
    return {
      ...base,
      attendee,
    };
  }
  return base;
}

function formatTimeOfDay(timeString: string): string {
  // Convert "09:00" or "14:30" to "9:00 AM" or "2:30 PM"
  const [hours, minutes] = timeString.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
}

export function meetingTemplateReferenceEventTimingSummary({
  referenceEvent,
  preferredTimeSlot,
  offset,
  offsetUnit,
  useSpecificTime,
  specificTimeOfDay,
}: MeetingReferenceEventTimingInfo) {
  if (!referenceEvent) return undefined;

  const referenceLabel = titleize(referenceEvent);

  // Determine the time display based on whether specific time is used
  let timeAddOn = "";
  if (useSpecificTime && specificTimeOfDay) {
    timeAddOn = `${formatTimeOfDay(specificTimeOfDay)}, `;
  } else if (preferredTimeSlot) {
    timeAddOn = `${titleize(preferredTimeSlot)}, `;
  }

  if (offset === 0) {
    return `${timeAddOn}${referenceLabel}`;
  }

  const displayoffSetUnit = ["day", "month", "year"].includes(offsetUnit) ? offsetUnit : titleize(offsetUnit);

  const direction = offset < 0 ? "before" : "after";
  const value = Math.abs(offset);

  const pluralUnit = value === 1 ? displayoffSetUnit : `${displayoffSetUnit}s`;

  return `${timeAddOn}${value} ${pluralUnit} ${direction} ${referenceLabel}`;
}

export function meetingTemplateRecurringEventTimingSummary({
  recurrenceRules,
  seriesStartsAt,
  timezone,
  pluralizeInterval = false,
  excludeTime = false,
}: Pick<MeetingRecurringTimingInfo, "recurrenceRules" | "seriesStartsAt" | "timezone"> & { pluralizeInterval?: boolean; excludeTime?: boolean; }) {
  if (!recurrenceRules) return undefined;

  const ruleSet = RRuleSet.fromString(recurrenceRules.join("\n"));
  let result = ruleSet.toText()
    .replace(/^every week/, "Weekly")
    .replace(/^every day/, "Daily")
    .replace(/^every month/, "Monthly")
    .replace(/^every year/, "Yearly");

  result = pluralizeInterval
    ? result.replace(/ [\w]+[nsir]day\b/, "$&s")
    : result;

  result = upperFirst(result);

  const time = dateInFormat(seriesStartsAt, "HH:mma", timezone);

  return excludeTime ? result : `${result}, ${time}`;
}

export function meetingTemplateTimingSummary(
  info: MeetingTimingInfo & { meetingType: MeetingTemplateType; },
): string | undefined {
  if (info.meetingType === "group_session_recurring") {
    return meetingTemplateRecurringEventTimingSummary({ pluralizeInterval: true, ...info });
  }
  return meetingTemplateReferenceEventTimingSummary(info);
}

const FIELD_LABELS: Record<string, string> = {
  name: "Meeting name",
  learningObjective: "Learning objective",
  leadCompositeId: "Meeting lead",
  attendees: "Attendees",
  seriesStartsAt: "Date and time",
  recurrenceRules: "Recurrence pattern",
  timezone: "Timezone",
  offset: "Date and time",
  offsetUnit: "Date and time",
  preferredTimeSlot: "Date and time",
  referenceEvent: "Date and time",
};

function hasTimingFieldChanges(dirtyFields: any): boolean {
  const timingFields = ["seriesStartsAt", "recurrenceRules", "timezone", "offset", "offsetUnit", "preferredTimeSlot", "referenceEvent"];
  return timingFields.some((field) => dirtyFields[field]);
}

function hasUserAttendeeChanges(originalAttendees: TemplateMeetingAttendee[] = [], newAttendees: TemplateMeetingAttendee[] = []): boolean {
  const originalUserAttendees = originalAttendees.filter((a) => a.attendeeType === "User");
  const newUserAttendees = newAttendees.filter((a) => a.attendeeType === "User");

  if (originalUserAttendees.length !== newUserAttendees.length) {
    return true;
  }

  const originalIds = new Set(originalUserAttendees.map((a) => a.attendeeId));
  const newIds = new Set(newUserAttendees.map((a) => a.attendeeId));

  return originalUserAttendees.some((a) => !newIds.has(a.attendeeId))
    || newUserAttendees.some((a) => !originalIds.has(a.attendeeId));
}

export function getRecurringMeetingChangedFields(
  dirtyFields: any,
  originalData: any,
  formData: any,
  meetingType: string,
): string[] {
  const changedFields: Set<string> = new Set();

  Object.keys(dirtyFields).forEach((field) => {
    if (field === "attendees" && hasUserAttendeeChanges(originalData?.attendees, formData?.attendees)) {
      changedFields.add("Attendees");
    } else if (field === "leadCompositeId") {
      if (meetingType === "group_session_adhoc" || meetingType === "group_session_recurring") {
        changedFields.add("Meeting lead");
      }
    } else if (field === "learningObjective") {
      if (meetingType === "group_session_adhoc" || meetingType === "group_session_recurring") {
        changedFields.add("Learning objective");
      }
    } else if (FIELD_LABELS[field]) {
      changedFields.add(FIELD_LABELS[field]);
    }
  });

  if (hasTimingFieldChanges(dirtyFields)) {
    changedFields.add("Date and time");
  }

  return Array.from(changedFields).sort();
}

export function shouldShowRecurringMeetingWarning(
  isCreate: boolean,
  isRecurring: boolean,
  changedFields: string[],
): boolean {
  return !isCreate && isRecurring && changedFields.length > 0;
}

/**
 * Transform API response data to form data
 * Includes form-specific fields and handles complex nested data
 *
 * @param data - MeetingTemplate from API response
 * @returns Schema-compatible data ready for form initialization
 */
export function toMeetingTemplateFormData(data: MeetingTemplate): MeetingTemplateSchema {
  const {
    paths = [],
    lead,
    leadId,
    leadType,
    ...rest
  } = data;

  return {
    ...rest,
    paths,
    leadCompositeId: leadId ? toCompositeId(leadType || "User", leadId) : "",
    allowWeekends: false,
  } as MeetingTemplateSchema;
}

/**
 * Transform form data to API create payload
 * Removes form-only fields and ensures correct structure for API
 */
export function toCreateMeetingTemplatePayload(formData: MeetingTemplateSchema): import("./models/payloads").CreateMeetingTemplatePayload {

  const { paths, leadCompositeId, ...rest } = formData;

  // Type assertion needed because form types differ from API types
  return rest as import("./models/payloads").CreateMeetingTemplatePayload;
}

/**
 * Transform form data to API update payload
 * Same as create but all fields optional
 */
export function toUpdateMeetingTemplatePayload(formData: Partial<MeetingTemplateSchema>): import("./models/payloads").UpdateMeetingTemplatePayload {

  const { paths, leadCompositeId, ...rest } = formData;

  return rest as import("./models/payloads").UpdateMeetingTemplatePayload;
}
